import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ShoppingBag, Zap, FileText, ChevronRight } from 'lucide-react';

const NAV_LINKS = [
  { label: 'Konfigurator', href: '#configurator' },
  { label: 'Produkte', href: '#categories' },
  { label: 'Marken', href: '#brands' },
  { label: 'Services', href: '#services' },
  { label: 'Kontakt', href: '#contact' },
];

const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <motion.nav
        data-testid="main-navigation"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
          transition: 'all 0.5s',
          background: scrolled ? 'rgba(10,10,15,0.85)' : 'transparent',
          backdropFilter: scrolled ? 'blur(16px)' : 'none',
          WebkitBackdropFilter: scrolled ? 'blur(16px)' : 'none',
          borderBottom: scrolled ? '1px solid rgba(255,255,255,0.06)' : 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '72px', width: '100%', padding: '0 1.5rem' }}>
          <a href="#hero" style={{ display: 'flex', alignItems: 'center', gap: '12px', textDecoration: 'none' }} data-testid="nav-logo">
            <img src="/images/trxl-shop-logo.png" alt="TrXL Performance" style={{ height: '42px', objectFit: 'contain' }} />
          </a>

          <div style={{ display: 'flex', alignItems: 'center', gap: '2rem' }} className="nav-desktop-links">
            {NAV_LINKS.map(link => (
              <a key={link.href} href={link.href} style={{
                textDecoration: 'none', fontFamily: 'var(--font-heading)', fontWeight: 600,
                fontSize: '0.85rem', letterSpacing: '0.12em', textTransform: 'uppercase',
                color: 'var(--fg-muted)', transition: 'color 0.3s',
              }}
                onMouseEnter={e => e.target.style.color = 'white'}
                onMouseLeave={e => e.target.style.color = 'var(--fg-muted)'}
              >{link.label}</a>
            ))}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <a href="/shop" className="btn-primary nav-shop-btn" style={{ padding: '0.6rem 1.5rem', fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }} data-testid="nav-shop-btn">
              <ShoppingBag size={14} /> Zum Shop
            </a>
            <button onClick={() => setMobileOpen(true)} className="nav-mobile-toggle"
              style={{ display: 'none', background: 'none', border: 'none', cursor: 'pointer', color: 'white', padding: '0.5rem' }}
              data-testid="mobile-menu-toggle"
            >
              <Menu size={24} />
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            style={{
              position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(5,5,5,0.98)',
              backdropFilter: 'blur(20px)', display: 'flex', flexDirection: 'column',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '1.5rem' }}>
              <button onClick={() => setMobileOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'white', padding: '0.5rem' }}>
                <X size={28} />
              </button>
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '0 2rem', gap: '0.5rem' }}>
              {NAV_LINKS.map((link, i) => (
                <motion.a key={link.href} href={link.href}
                  initial={{ x: 80, opacity: 0 }} animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: i * 0.1 }}
                  onClick={() => setMobileOpen(false)}
                  style={{
                    textDecoration: 'none', fontFamily: 'var(--font-heading)', fontWeight: 700,
                    fontSize: '2rem', letterSpacing: '0.05em', textTransform: 'uppercase',
                    color: 'white', padding: '0.75rem 0', borderBottom: '1px solid rgba(255,255,255,0.06)',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  }}
                >
                  {link.label} <ChevronRight size={20} style={{ color: 'var(--primary)' }} />
                </motion.a>
              ))}
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
                style={{ marginTop: '2rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <a href="/shop" onClick={() => setMobileOpen(false)} className="btn-primary"
                  style={{ textAlign: 'center', padding: '1rem', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                  <ShoppingBag size={18} /> Zum Shop
                </a>
                <a href="/chiptuning" onClick={() => setMobileOpen(false)}
                  style={{ textDecoration: 'none', textAlign: 'center', padding: '1rem', fontSize: '1rem', color: 'var(--fg-muted)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                  <Zap size={18} /> Chiptuning
                </a>
                <a href="/fileservice" onClick={() => setMobileOpen(false)}
                  style={{ textDecoration: 'none', textAlign: 'center', padding: '1rem', fontSize: '1rem', color: 'var(--fg-muted)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                  <FileText size={18} /> File-Service
                </a>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        @media (max-width: 768px) {
          .nav-desktop-links { display: none !important; }
          .nav-shop-btn { display: none !important; }
          .nav-mobile-toggle { display: block !important; }
        }
      `}</style>
    </>
  );
};

export default Navigation;
